/**
 * Manufacturer-declared maximum speed of an aircraft (`max_speed_ms`, the SORA 2.5 Table 2 speed input).
 *
 * POST /aircraft requires a positive value (400 aircraft_max_speed_required). PUT /aircraft/{id} treats null as
 * "not sent" and rejects only a newly sent value <= 0 (aircraft_max_speed_invalid), so an aircraft stored before the
 * speed became required — without one or with one <= 0 — stays editable; the form only warns, like the
 * characteristic dimension.
 */

/** The aircraft has no usable speed: SORA falls back to the simplified MTOM/altitude estimate for it. */
export const isMaxSpeedMissing = (aircraft) => !(Number(aircraft?.max_speed_ms) > 0);

/** The form requires the speed on create and when the edited aircraft already has a usable one (it cannot be cleared). */
export const isMaxSpeedRequired = (editingAircraft) => !editingAircraft || !isMaxSpeedMissing(editingAircraft);

/**
 * Locale key (in `t.fleet`) of the speed field's helper text: required on create and for an aircraft with a usable
 * speed, only recommended for an aircraft stored without one.
 */
export const maxSpeedHelperKey = (editingAircraft) =>
  isMaxSpeedRequired(editingAircraft) ? 'maxSpeedHelper' : 'maxSpeedHelperRecommended';

/**
 * Locale key (in `t.fleet`) of the form's speed error, or null when the value passes: empty where required →
 * maxSpeedRequired, a filled value that is not a positive number → maxSpeedPositive, except the unchanged stored
 * value <= 0 of an aircraft saved before the speed became required (the partial PUT does not send it).
 */
export const maxSpeedValidationKey = (value, editingAircraft) => {
  const empty = value === undefined || value === null || String(value).trim() === '';
  if (empty) return isMaxSpeedRequired(editingAircraft) ? 'maxSpeedRequired' : null;
  if (Number(value) > 0) return null;
  const unchangedStoredValue = editingAircraft?.max_speed_ms != null
    && Number(value) === Number(editingAircraft.max_speed_ms);
  return unchangedStoredValue ? null : 'maxSpeedPositive';
};
